import { useContext, useState } from "react";
import PropTypes from "prop-types";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { Box, Button, Divider, InputAdornment, Typography } from "@mui/material";
import { Lock, Person } from "@mui/icons-material";
import { Navigate, useNavigate } from "react-router-dom";

import NonLoggedInLayout from "../../layout/NonLoggedInLayout";
import CommonTextField from "../common/CommonTextField";
import CounsellorAuthContext from "../../context/CounsellorAuthContext";
import {
  counsellorGoogleLogin,
  counsellorLogin,
} from "../../services/counsellorPortalApi";
import { useToast } from "../../hook/useToast";

const counsellorGoogleClientId =
  import.meta.env.VITE_COUNSELLOR_GOOGLE_CLIENT_ID ||
  import.meta.env.VITE_GOOGLE_CLIENT_ID;

const getLoginErrorMessage = (error) => {
  const data = error?.response?.data;
  const errors = data?.errors || data;

  if (Array.isArray(errors?.non_field_errors)) {
    return errors.non_field_errors[0];
  }

  if (Array.isArray(errors?.detail)) {
    return errors.detail[0];
  }

  if (errors && typeof errors === "object") {
    const firstFieldError = Object.values(errors).find(Array.isArray)?.[0];
    if (firstFieldError) {
      return firstFieldError;
    }
  }

  return errors?.detail || data?.message || "Invalid email or password";
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F5F5F5",
    borderRadius: "12px",
    height: "48px",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
  "& .MuiOutlinedInput-input": { padding: "12px 14px" },
};

const CounsellorGoogleLoginButton = ({ disabled, onGoogleLogin }) => {
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    scope: "openid email profile",
    ux_mode: "popup",
    onSuccess: ({ code }) => onGoogleLogin(code),
    onError: () => onGoogleLogin(null),
  });

  return (
    <Button
      type="button"
      disabled={disabled}
      onClick={() => googleLogin()}
      fullWidth
      variant="outlined"
      sx={{
        py: 1.2,
        borderRadius: "12px",
        borderColor: "#ECEFF4",
        color: "#5D667A",
        backgroundColor: "#fff",
        fontSize: "15px",
        fontWeight: 600,
        textTransform: "none",
        gap: 1,
        "&:hover": {
          borderColor: "#D9DEE8",
          backgroundColor: "#F8FAFC",
        },
      }}
    >
      <Box
        component="img"
        src="/assets/images/google.svg"
        alt=""
        sx={{ width: 20, height: 20 }}
      />
      Continue with Google
    </Button>
  );
};

CounsellorGoogleLoginButton.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onGoogleLogin: PropTypes.func.isRequired,
};

const CounsellorLoginPage = () => {
  const { showToast } = useToast();
  const { counsellorUser, loginCounsellor, portal } = useContext(CounsellorAuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  if (counsellorUser?.role === portal.role) {
    return <Navigate to={`${portal.basePath}/dashboard`} replace />;
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const completeCounsellorLogin = (response, successMessage) => {
    const accessToken = response?.data?.token?.access;

    if (!accessToken) {
      showToast("Login failed", "error");
      return;
    }

    loginCounsellor(
      accessToken,
      response.data.user,
      response.data.counsellor,
      response.data.dashboard
    );
    showToast(successMessage, "success");
    navigate(`${portal.basePath}/dashboard`);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.email || !formData.password) {
      showToast("Email and password are required", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await counsellorLogin(formData);
      completeCounsellorLogin(response, `${portal.label} login successful`);
    } catch (error) {
      showToast(getLoginErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (code) => {
    if (!code) {
      showToast("Google sign-in was cancelled", "error");
      return;
    }

    setGoogleLoading(true);
    try {
      const response = await counsellorGoogleLogin({ code });
      completeCounsellorLogin(response, `${portal.label} Google login successful`);
    } catch (error) {
      showToast(getLoginErrorMessage(error), "error");
    } finally {
      setGoogleLoading(false);
    }
  };

  const isSubmitting = loading || googleLoading;

  return (
    <NonLoggedInLayout
      leftImage="/assets/images/admin-dashboard-login.png"
      leftImageAlt={`${portal.label} portal login`}
    >
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 450,
            p: 4,
            borderRadius: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: 700,
                color: "#2D315F",
                letterSpacing: 0,
                lineHeight: 1.1,
              }}
            >
              {portal.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: 700,
                color: "#FFB04A",
                letterSpacing: 0,
                lineHeight: 1.1,
              }}
            >
              Portal
            </Typography>
            <Box
              component="span"
              sx={{
                display: "block",
                width: 60,
                height: 3,
                bgcolor: "#2D315F",
                mt: 1,
                borderRadius: 2,
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: "14px",
              color: "rgba(129, 143, 171, 1)",
              mt: 1,
              mb: 3,
              fontWeight: 500,
            }}
          >
            Sign in to access the {portal.label.toLowerCase()} dashboard
          </Typography>

          <form onSubmit={handleSubmit}>
            <CommonTextField
              placeholder="E-mail"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="email"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ ...fieldSx, mb: 1.5 }}
            />

            <CommonTextField
              placeholder="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              autoComplete="current-password"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ ...fieldSx, mb: 1 }}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              fullWidth
              sx={{
                mt: 0.7,
                py: 1.2,
                borderRadius: "12px",
                backgroundColor: "#2D315F",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { backgroundColor: "#252957" },
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            <Divider sx={{ my: 2.5, color: "#818FAB", fontSize: "13px" }}>
              OR
            </Divider>

            {counsellorGoogleClientId ? (
              <GoogleOAuthProvider clientId={counsellorGoogleClientId}>
                <CounsellorGoogleLoginButton
                  disabled={isSubmitting}
                  onGoogleLogin={handleGoogleLogin}
                />
              </GoogleOAuthProvider>
            ) : (
              <Button
                type="button"
                disabled
                fullWidth
                variant="outlined"
                sx={{
                  py: 1.2,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Continue with Google
              </Button>
            )}
          </form>
        </Box>
      </Box>
    </NonLoggedInLayout>
  );
};

export default CounsellorLoginPage;
